import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import MilkFixedService from '~/api/services/milkFixedPrice'
import Layout from '~/components/Layout'
import notify from '~/components/Notify'
import { RouteTypes } from '~/routes/constants'
import { useAuth } from '~/routes/components/AuthProvider'
import TableComponent from '~/components/TableComponent'
import i18n from '~/components/i18n'
import Dialog from '~/components/Dialog'
import CustomInput from '~/components/CustomInput'
import ProfileSettingsService from '~/api/services/profileSettings'
import { getAbortOfferColumns } from './util'
import TwoFaComp from '~/components/TwoFaComp'
import ExcelExport from '~/components/ExcelExport'
import { handleErrors } from '~/utils/helpers'
import { MilkFixedPriceQuery } from '~/QueryHooks/MilkFixePriceQuery'

const defaultFilter = {
  page: 0,
  rows: 10
}

const getActions = () => [
  {
    popupMessage: i18n.t('Change offer amount'),
    type: 'icon',
    name: 'pencil alternate',
    size: 'large',
    actionType: 'edit',
    display: ['in_progress'],
    displayedTextKey: 'statusLabel',
    displayedText: i18n.t('No actions')
  },
  {
    popupMessage: i18n.t('Cancel offer amount'),
    type: 'icon',
    name: 'close',
    size: 'large',
    color: 'red',
    actionType: 'close',
    display: ['in_progress'],
    displayedText: i18n.t('No actions')
  }
]

const getDefaultTableColumnHeaders = (priceLabel, amountUnit) => [
  {
    name: i18n.t('Trading month'),
    key: 'date',
    type: 'dateRibbon',
    format: 'MMM YYYY'
  },
  {
    name: i18n.t('Status'),
    key: 'statusLabel'
  },
  {
    name: i18n.t('Fixed price {{priceLabel}}', { priceLabel }),
    key: 'price',
    type: 'no-unit-number'
  },
  {
    name: i18n.t('Supplier No.'),
    key: 'supplierNo'
  },
  {
    name: i18n.t('Offer ID'),
    key: 'uniqueId'
  },
  {
    name: i18n.t('Offer date'),
    key: 'createdAt',
    type: 'date',
    format: 'DD.MM.YYYY HH:mm'
  },
  {
    name: i18n.t('Offered amount (in {{amountUnit}})', { amountUnit }),
    key: 'amount',
    type: 'number-whole'
  },
  {
    name: i18n.t('Confirmed amount (in {{amountUnit}})', { amountUnit }),
    key: 'confirmedAmount',
    type: 'number-whole'
  },
  {
    name: i18n.t('Confirmation date'),
    key: 'confirmationDate',
    type: 'date',
    format: 'DD.MM.YYYY HH:mm'
  },
  {
    name: i18n.t('Total (in €)'),
    key: 'total',
    type: 'price-total'
  }
]

const Results: React.FC = () => {
  const { t } = useTranslation()
  const { id } = useParams()
  const actions = getActions()
  const { systemData, systemData: { amountUnit, amountVolume, amountPrice } } = useAuth()
  const priceLabel = `(${amountPrice == 'e' ? '€' : '€ Ct'}/${amountVolume > 1 ? amountVolume : ''}${amountUnit})`
  const tfaOfferEdit = systemData?.roleSettings?.tfaOffer === 1
  const tfaOfferAbort = systemData?.roleSettings?.tfaAbortAmount === 1
  const tfaType = systemData?.logged?.tfaType
  
  const [isBusy, setIsBusy] = useState(false)
  const [correctTfaCode, setCorrectTfaCode] = useState(false)
  const [tfaCode, setTfaCode] = useState('')
  const [dataFiltered, setDataFiltered] = useState(defaultFilter)
  const [editAmount, setEditAmount] = useState(null)
  const [cancelAmount, setCancelAmount] = useState(null)
  const [fileSource, setFileSource] = useState(null)
  const [amount, setAmount] = useState<string | number>(0)
  const abbortOffersColumnHeaders = getAbortOfferColumns(amountUnit, priceLabel)
  const [abbortOffersRows, setAbbortOffersRows] = useState([])
  const [tableColumnHeaders, setTableColumnHeaders] = useState(getDefaultTableColumnHeaders(priceLabel, amountUnit))

  const changeAmountHeaders = [
    {
      name: t('Trading month'),
      key: 'date',
      type: 'dateRibbon',
      format: 'MMM YYYY'
    },
    {
      name: t('Offered amount (in {{amountUnit}})', { amountUnit }),
      key: 'amount',
      type: 'number-whole'
    },
    {
      name: t('Minimum contingent (in {{amountUnit}})', { amountUnit }),
      key: 'minimumContingent',
      type: 'number-whole'
    },
    {
      name: t('Max of trading amount (in {{amountUnit}})', { amountUnit }),
      key: 'maxTradingAmount',
      type: 'number-whole'
    },
    {
      name: t('New amount offer (in {{amountUnit}})', { amountUnit }),
      key: 'amount',
      type: 'input-confirm'
    }
  ]

  const {
    data: showData,
    isLoading: isBusyShow,
    refetch: fetchShowData
  } = MilkFixedPriceQuery.showDataTradingSlot(id)

  const {
    data: resultsData,
    isLoading: isBusyResultsData,
    refetch: fetchResultsData,
    isFetching: isFetchingResults
  } = MilkFixedPriceQuery.getPreviewOffersData(id, { ...dataFiltered, page: dataFiltered.page === 0 ? dataFiltered.page : dataFiltered.page - 1 })

  const {
    data: resultsSettings,
    isLoading: isBusyResultsSettings,
    refetch: fetchOfferAmountSettings
  } = MilkFixedPriceQuery.farmerOfferAmountsSettings(id)

  useEffect(() => {
    fetchShowData()
    fetchResultsData()
    setTableColumnHeaders(getDefaultTableColumnHeaders(priceLabel, amountUnit))
  }, [t])

  useEffect(() => {
    fetchResultsData()
    fetchOfferAmountSettings()
  }, [dataFiltered])

  useEffect(() => {
    if(resultsSettings?.hiddenPrices === 1) {
      setTableColumnHeaders(tableColumnHeaders.filter(column => column.key !== 'total'))
    }
  }, [resultsSettings?.hiddenPrices])

  function renderModalPreviewFile() {
    return <Dialog
      open={Boolean(fileSource)}
      handleClose={() => setFileSource(null)}
      textContent={<iframe width='100%' height='100%' src={fileSource}></iframe>}
      textHeader={t('Preview')}
      textClose={t('Close')}
      type={'preview'}
      isLoading={isBusy}
    />
  }

  async function getChangeAmountData(row) {
    try {
      if (tfaOfferEdit && (tfaType === 'email' || tfaType === 'sms')) {
        await ProfileSettingsService.sendCode()
        notify(t('Code sent'), 'success')
      }
      const response = await MilkFixedService.getChangeAmountData(id, row.id)
      setEditAmount({
        id: row.id,
        ...response.data
      })
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false)
  }

  async function handleSendCode(row) {
    setCancelAmount(row)
    if (tfaType === 'email' || tfaType === 'sms') {
      setIsBusy(true)
      try {
        await ProfileSettingsService.sendCode()
        notify(t('Code sent'), 'success')
      } catch (error) {
        setCancelAmount(null)
        handleErrors(error)
      }
      setIsBusy(false)
    }
    setIsBusy(false)
  }

  async function handleCloseAction(row) {
    setIsBusy(true)
    try {
      const response = await MilkFixedService.abortTradingSlotOfferData(id, row.id)
      setAbbortOffersRows(response.data)
      if (tfaOfferAbort) {
        handleSendCode(row)
        return
      }
      setCancelAmount(row)
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false)
  }

  function handleClickAction(row, type) {
    switch (type) {
      case 'edit':
        getChangeAmountData(row)
        return
      case 'close':
        handleCloseAction(row)
        return
      default:
        return
    }
  }

  function renderResultsData() {
    const actionsFiltered = resultsSettings?.canChangeAmounts ? actions : actions.filter(action => action.actionType !== 'edit')
    return <TableComponent
      tableColumnHeaders={resultsSettings?.farmerOfferChange && showData?.status === 'active' ? [...tableColumnHeaders, { name: t('Actions') }] : tableColumnHeaders}
      actions={resultsSettings?.farmerOfferChange && showData?.status === 'active' ? actionsFiltered : []}
      dataFiltered={dataFiltered}
      handleClickAction={handleClickAction}
      animated
      isFetchingData={isFetchingResults}
      rows={rows}
      total={filtered}
      handleChangeDataFiltered={(data) => setDataFiltered(data)}
    />
  }

  function renderActions() {
    return <ExcelExport
      url={`trading-slots/${id}/offers/export`}
      fileName={'trading_slot_results'}
    />
  }

  function renderInputConfirm(type, value) {
    switch (type) {
      case 'input-confirm':
        return <CustomInput
          value={amount}
          error={verifyInputField()}
          onChange={(value) => setAmount(value || '')}
        />
      default:
        return
    }
  }

  async function handleChangeOfferAmount(data?) {
    setIsBusy(false)
    try {
      await MilkFixedService.changeAmountData(id, editAmount.id, { amount: parseInt(amount.toString()), ...data })
      setEditAmount(null)
      setAmount('')
      notify(t('Trading amount has been successfully changed'), 'success')
      fetchResultsData()
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false)
  }

  function verifyInputField() {
    const disableBellowMin = Boolean(resultsSettings?.minContingentOffer)
    const enableExcidMax = Boolean(resultsSettings?.exceedingMaxAllowed)
    return (disableBellowMin ? (parseInt(amount.toString()) < editAmount?.minimumContingent) : false) ||
      (enableExcidMax ? false : (parseInt(amount.toString()) > parseInt(editAmount?.maxTradingAmount)))
  }

  function renderEditAmountModal() {
    return <Dialog
      isDialogOpen={Boolean(editAmount)}
      handleClose={() => {
        setAmount('')
        setEditAmount(null)
        setTfaCode('')
        setCorrectTfaCode(false)
      }}
      htmlContent
      isLoading={isBusy}
      style={{ width: '60%' }}
      textContent={<>
        <TableComponent
          tableColumnHeaders={changeAmountHeaders}
          renderColumn={(type, value) => renderInputConfirm(type, value)}
          removePagination
          rows={[editAmount]}
          total={1}
        />
        <div>
          {tfaOfferEdit && <TwoFaComp
            onChangeTfaCode={(code) => setTfaCode(code)}
            onChangeCorrectTfaCode={(correctTfaCode) => setCorrectTfaCode(correctTfaCode)}
          />}
        </div>
      </>}
      handleSubmit={() => handleChangeOfferAmount(tfaOfferEdit ? { code: tfaCode } : null)}
      disableSubmit={tfaOfferEdit ? !(correctTfaCode && !verifyInputField()) : verifyInputField()}
      textSubmit={t('Change amount')}
      textClose={t('Abort')}
      textHeader={t('Change existing trading amount')}
      type={'dialog'}
    />
  }

  async function abortTradingSlotOffer(data?) {
    setIsBusy(true)
    try {
      await MilkFixedService.abortTradingSlotOffer(id, cancelAmount?.id, { ...data })
      setCancelAmount(null)
      fetchResultsData()
      notify(t('Farmer amount blocked succesfully'), 'success')
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false)
  }

  function renderCancelAmountModal() {
    return <Dialog
      isDialogOpen={Boolean(cancelAmount)}
      handleClose={() => {
        setCancelAmount(null)
        setTfaCode('')
        setCorrectTfaCode(false)
      }}
      disableSubmit={tfaOfferAbort ? !correctTfaCode : false}
      textSubmit={t('Confirm')}
      textClose={t('Abort')}
      textHeader={t('Delete')}
      textContent={<>
        <TableComponent
          tableColumnHeaders={abbortOffersColumnHeaders}
          rows={abbortOffersRows}
          total={abbortOffersRows.length}
          removePagination
        />
        <div>
          {tfaOfferAbort && <TwoFaComp
            onChangeTfaCode={(code) => setTfaCode(code)}
            onChangeCorrectTfaCode={(correctTfaCode) => setCorrectTfaCode(correctTfaCode)}
          />}
        </div>
      </>
      }
      handleSubmit={() => abortTradingSlotOffer(tfaOfferAbort ? { code: tfaCode } : null)}
    />
  }

  const { filtered, rows } = resultsData ? resultsData : { filtered: 0, rows: [] }
  const uniqueId = showData?.uniqueId || ''
  const loading = isBusyResultsData || isBusyResultsSettings || isBusyShow

  return <Layout
    isBusy={isBusy || loading}
    segmented
    title={t('Trading slot ID: {{uniqueId}}', { uniqueId })}
    close={RouteTypes.MILK_FIXED_PRICE}
    actions={renderActions()}
  >
    {showData && renderModalPreviewFile()}
    {editAmount && renderEditAmountModal()}
    {cancelAmount && renderCancelAmountModal()}
    {resultsData && resultsSettings && renderResultsData()}
  </Layout>
}
export default Results
